import { DatasourceDomain } from '@mode/shared/contract-common';
import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import * as DatasourceActions from './datasource.actions';

export const datasourceFeatureKey = 'datasource';
export const datasourcesUIAdapter = createEntityAdapter<DatasourceDomain.DatasourceUI>({
  selectId: (entity) => entity.token,
});

export interface State {
  datasources: ReadonlyArray<DatasourceDomain.Datasource>;
  isDatasourcesLoading: boolean;
  isDatasourcesComplete: boolean;
  datasourcesError: Error | null;

  reportDatasources: ReadonlyArray<DatasourceDomain.Datasource>;
  isReportDatasourcesLoading: boolean;
  isReportDatasourcesComplete: boolean;
  reportDatasourcesError: Error | null;

  reportDatasourcesUI: EntityState<DatasourceDomain.DatasourceUI>;
}

export const initialState: State = {
  datasources: [],
  isDatasourcesLoading: false,
  isDatasourcesComplete: false,
  datasourcesError: null,

  reportDatasources: [],
  isReportDatasourcesLoading: false,
  isReportDatasourcesComplete: false,
  reportDatasourcesError: null,

  reportDatasourcesUI: datasourcesUIAdapter.getInitialState(),
};

export const datasourcesReducer = createReducer(
  initialState.datasources,
  on(DatasourceActions.loadDatasources, (state) => state),
  on(DatasourceActions.loadDatasourcesSuccess, (state, action) => action.datasources),
  on(DatasourceActions.loadDatasourcesFailure, (state, action) => [])
);

export const isDatasourcesLoadingReducer = createReducer(
  initialState.isDatasourcesLoading,
  on(DatasourceActions.loadDatasources, (state) => true),
  on(DatasourceActions.loadDatasourcesSuccess, (state, action) => false),
  on(DatasourceActions.loadDatasourcesFailure, (state, action) => false)
);

export const isDatasourcesCompleteReducer = createReducer(
  initialState.isDatasourcesComplete,
  on(DatasourceActions.loadDatasources, (state) => false),
  on(DatasourceActions.loadDatasourcesSuccess, (state, action) => true),
  on(DatasourceActions.loadDatasourcesFailure, (state, action) => true)
);

export const datasourcesErrorReducer = createReducer(
  initialState.datasourcesError,
  on(DatasourceActions.loadDatasources, (state) => null),
  on(DatasourceActions.loadDatasourcesFailure, (state, action) => action.error)
);

export const reportDatasourcesReducer = createReducer(
  initialState.reportDatasources,
  on(DatasourceActions.loadReportDatasources, (state) => state),
  on(DatasourceActions.loadReportDatasourcesSuccess, (state, action) => action.datasources),
  on(DatasourceActions.loadReportDatasourcesFailure, (state, action) => [])
);

export const isReportDatasourcesLoadingReducer = createReducer(
  initialState.isReportDatasourcesLoading,
  on(DatasourceActions.loadReportDatasources, (state) => true),
  on(DatasourceActions.loadReportDatasourcesSuccess, (state, action) => false),
  on(DatasourceActions.loadReportDatasourcesFailure, (state, action) => false)
);

export const isReportDatasourcesCompleteReducer = createReducer(
  initialState.isReportDatasourcesComplete,
  on(DatasourceActions.loadReportDatasources, (state) => false),
  on(DatasourceActions.loadReportDatasourcesSuccess, (state, action) => true),
  on(DatasourceActions.loadReportDatasourcesFailure, (state, action) => true)
);

export const reportDatasourcesErrorReducer = createReducer(
  initialState.reportDatasourcesError,
  on(DatasourceActions.loadReportDatasources, (state) => null),
  on(DatasourceActions.loadReportDatasourcesFailure, (state, action) => action.error)
);

export const reportDatasourcesUIReducer = createReducer(
  initialState.reportDatasourcesUI,
  on(DatasourceActions.loadReportDatasourcesSuccess, (state, action) =>
    datasourcesUIAdapter.setAll(
      action.datasources.map((ds) => ({
        token: ds.token,
        refreshing: false,
        refreshComplete: false,
        refreshError: null,
      })),
      state
    )
  ),
  on(DatasourceActions.refreshDatasource, (state, action) =>
    datasourcesUIAdapter.setOne(
      {
        token: action.token,
        refreshComplete: false,
        refreshError: null,
        refreshing: true,
      },
      state
    )
  ),
  on(DatasourceActions.refreshDatasourceSuccess, (state, action) =>
    datasourcesUIAdapter.setOne(
      {
        token: action.token,
        refreshComplete: true,
        refreshError: null,
        refreshing: false,
      },
      state
    )
  ),
  on(DatasourceActions.refreshDatasourceFailure, (state, action) =>
    datasourcesUIAdapter.setOne(
      {
        token: action.token,
        refreshComplete: true,
        refreshError: action.error,
        refreshing: false,
      },
      state
    )
  )
);

export const reducer: ActionReducerMap<State> = {
  datasources: datasourcesReducer,
  isDatasourcesLoading: isDatasourcesLoadingReducer,
  isDatasourcesComplete: isDatasourcesCompleteReducer,
  datasourcesError: datasourcesErrorReducer,

  reportDatasources: reportDatasourcesReducer,
  isReportDatasourcesLoading: isReportDatasourcesLoadingReducer,
  isReportDatasourcesComplete: isReportDatasourcesCompleteReducer,
  reportDatasourcesError: reportDatasourcesErrorReducer,

  reportDatasourcesUI: reportDatasourcesUIReducer,
};
